import { LINK_WINDOW_DAYS } from '@klopotun/domain';
import { useState } from 'react';
import {
  type LinkSubscription,
  useLinkOperationMutation,
  useOperationLinkOptionsQuery,
} from '../../../entities/bank-transaction/index.js';
import { formatStoredDateForPeople } from '../../../features/payment-view/index.js';
import {
  BottomSheet,
  Button,
  EmptyState,
  ErrorState,
  SelectField,
  type SelectOption,
  Spinner,
} from '../../../shared/ui/index.js';
import { SummaryNote } from '../../../widgets/year-summary/index.js';
import {
  amountMismatchTextOf,
  linkedMoneyTextOf,
  linkPeriodLabelOf,
} from '../lib/link-period-text.js';
import styles from './link-subscription-sheet.module.css';

type LinkSubscriptionSheetProps = {
  operationId: string;
  occurredOn: string;
  onClose: () => void;
};

type LinkFormProps = {
  operationId: string;
  occurredOn: string;
  subscriptions: LinkSubscription[];
  onDone: () => void;
};

const LinkForm = ({ operationId, occurredOn, subscriptions, onDone }: LinkFormProps) => {
  const [paymentId, setPaymentId] = useState('');
  const [occurrenceId, setOccurrenceId] = useState('');
  const link = useLinkOperationMutation();

  const chosenSubscription = subscriptions.find(
    (subscription) => subscription.paymentId === paymentId,
  );
  const chosenPeriod = chosenSubscription?.periods.find(
    (period) => period.occurrenceId === occurrenceId,
  );
  const mismatchText = chosenPeriod === undefined ? null : amountMismatchTextOf(chosenPeriod);
  const subscriptionOptions: SelectOption[] = subscriptions.map((subscription) => ({
    value: subscription.paymentId,
    label: subscription.name,
  }));
  const periodOptions: SelectOption[] = (chosenSubscription?.periods ?? []).map((period) => ({
    value: period.occurrenceId,
    label: linkPeriodLabelOf(period),
  }));

  const chooseSubscription = (value: string): void => {
    const subscription = subscriptions.find((candidate) => candidate.paymentId === value);

    link.reset();
    setPaymentId(value);
    setOccurrenceId(subscription?.preselectedOccurrenceId ?? '');
  };

  const confirm = (): void => {
    if (chosenPeriod === undefined) {
      return;
    }

    link.mutate({ operationId, occurrenceId: chosenPeriod.occurrenceId }, { onSuccess: onDone });
  };

  return (
    <>
      <SelectField
        label="Підписка"
        name="link-subscription"
        options={subscriptionOptions}
        value={paymentId}
        placeholder="Оберіть підписку"
        disabled={link.isPending}
        onChange={(event) => {
          chooseSubscription(event.target.value);
        }}
      />

      {chosenSubscription !== undefined && (
        <SelectField
          label="Платіж"
          name="link-period"
          options={periodOptions}
          value={occurrenceId}
          disabled={link.isPending}
          onChange={(event) => {
            link.reset();
            setOccurrenceId(event.target.value);
          }}
        />
      )}

      {mismatchText !== null && <SummaryNote tone="warning">{mismatchText}</SummaryNote>}

      {chosenPeriod !== undefined && (
        <SummaryNote tone="hint">
          Банк підтвердить цей платіж: {linkedMoneyTextOf(chosenPeriod)},{' '}
          {formatStoredDateForPeople(occurredOn)}.
          {chosenPeriod.status === 'paid' && ' Оплату, внесену вручну, замінять дані банку.'} Далі
          списання цього продавця біля дати платежу підтверджуватимуть цю підписку самі.
        </SummaryNote>
      )}

      {link.isError && (
        <p className={styles.error} role="alert">
          {link.error.message}
        </p>
      )}

      <Button disabled={chosenPeriod === undefined} busy={link.isPending} onClick={confirm}>
        Підтвердити оплату
      </Button>
    </>
  );
};

type LinkSheetBodyProps = {
  operationId: string;
  occurredOn: string;
  onDone: () => void;
};

const LinkSheetBody = ({ operationId, occurredOn, onDone }: LinkSheetBodyProps) => {
  const options = useOperationLinkOptionsQuery(operationId);

  if (options.isPending) {
    return (
      <div className={styles.centered}>
        <Spinner label="Завантаження" />
      </div>
    );
  }

  if (options.isError) {
    return (
      <ErrorState
        title="Не вдалося завантажити підписки"
        message={options.error.message}
        onRetry={() => {
          void options.refetch();
        }}
      />
    );
  }

  if (options.data.subscriptions.length === 0) {
    return (
      <EmptyState
        title="Немає платежу поруч"
        description={`Немає платежу без підтвердження банком за ${String(LINK_WINDOW_DAYS)} днів до чи після цієї операції.`}
      />
    );
  }

  return (
    <LinkForm
      operationId={operationId}
      occurredOn={occurredOn}
      subscriptions={options.data.subscriptions}
      onDone={onDone}
    />
  );
};

export const LinkSubscriptionSheet = ({
  operationId,
  occurredOn,
  onClose,
}: LinkSubscriptionSheetProps) => (
  <BottomSheet open title="Оплата підписки" onClose={onClose}>
    <div className={styles.body}>
      <LinkSheetBody operationId={operationId} occurredOn={occurredOn} onDone={onClose} />
    </div>
  </BottomSheet>
);
